import React, { useState, useCallback } from "react";
import { race20CardImg } from "../../diamondCasinoAllGames/race20/race20Cards";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";

const KING_IMGS = ["KSS", "KHH", "KCC", "KDD"];
// "Total points" / "Total cards" No(lay) / Yes(back) sizes as the reference shows them (0 when not OPEN)
const YN_SIZES = [
  { lay: "100", back: "100" },
  { lay: "105", back: "90" },
];
// Win-with columns: fancy indexes (reference fancy[2..7] = Win with 5, 6, 7, 15, 16, 17)
const WIN_WITH_COLS = [
  [2, 5],
  [3, 6],
  [4, 7],
];

/** Reference v-kvalmin / v-kvalmax: 1000+ -> K, 100000+ -> L (2 decimals, trailing zeros dropped) */
function formatLimit(value) {
  const n = Number(value) || 0;
  if (n >= 1e5) return `${parseFloat((n / 1e5).toFixed(2))}L`;
  if (n >= 1e3) return `${parseFloat((n / 1e3).toFixed(2))}K`;
  return String(parseFloat(n.toFixed(2)));
}

function getExposureForNat(exposures, nat) {
  if (!nat) return 0;
  return exposures[nat] ?? exposures[nat.toLowerCase()] ?? exposures[nat.trim()] ?? 0;
}

// Total points / Total cards bets are stored with their line ("Total cards 13"), so no
// exposure row matches the cell; like the reference admin it shows minus the sum of the
// line's stakes, back or lay. null when the market has no bets.
function lineBook(myBets, nat) {
  const re = new RegExp(`^${nat} \\d+$`, "i");
  const stakes = myBets
    .filter((b) => re.test(String(b.selection || b.player_name || "").trim()) && parseFloat(b.odds))
    .map((b) => parseFloat(b.stake ?? b.amount) || 0);
  return stakes.length ? -Math.round(stakes.reduce((a, v) => a + v, 0) * 100) / 100 : null;
}

const bookClass = (v) => (Number(v) > 0 ? "book-green" : "book-red");
// Locked cell: padlock at the top only when the runner has a book (no book → centred)
const lockCls = (open, exp) => (open ? "" : Number(exp) ? " suspended lock-top" : " suspended");
const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);

/**
 * Race 20 bet table — reference markup (index.css `.race20 …`), rendered inside the page's
 * `.casino-detail`: row of the 4 kings (back/lay), then Total points / Total cards (No/Yes)
 * and the Win-with grid. Clicks call onBetClick(value, label, type, item) as before.
 */
const BetTableRace20 = ({ data = [], onBetClick, exposures = {}, myBets = [] }) => {
  const [openCollapse, setOpenCollapse] = useState(() => new Set());
  const toggleCollapse = useCallback((id) => {
    setOpenCollapse((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const main = data.filter((s) => s?.subtype === "race20").sort(bySr);
  const fancy = data.filter((s) => s?.subtype === "race20fancy").sort(bySr);

  const rangeIcon = (id, item, extraClass = " ml-2") => (
    <div className={`range-icon d-inline-block${extraClass}`}>
      <i className="fas fa-info-circle float-right" onClick={() => toggleCollapse(id)} />{" "}
      <Collapse id={id} open={openCollapse.has(id)} className="icon-range">
        R:<span>{formatLimit(item?.min)}</span>-<span>{formatLimit(item?.max)}</span>
      </Collapse>
    </div>
  );

  const click = (item, value, label, type) => {
    if (item?.gstatus !== "OPEN") return;
    onBetClick?.(value, label, type, item);
  };

  const winWithBox = (idx) => {
    const item = fancy[idx];
    if (!item) return null;
    const open = item.gstatus === "OPEN";
    const exp = getExposureForNat(exposures, item.nat);
    return (
      <div className="casino-box-row">
        <div className="casino-nation-name">
          <b>{item.nat}</b> {rangeIcon(`fancy${idx}`, item)}
        </div>{" "}
        <div className="casino-bl-box">
          <div
            className={`back casino-bl-box-item${lockCls(open, exp)}`}
            onClick={() => click(item, item.b, item.nat, "back")}
          >
            <span className="casino-box-odd">{item.b}</span>
          </div>
        </div>{" "}
        <div className={`casino-nation-name rf-minheight ${bookClass(exp)}`}>{formatBook(exp)}</div>
      </div>
    );
  };

  return (
    <>
      <div className="row row5">
        {main.map((item, idx) => {
          const open = item.gstatus === "OPEN";
          const exp = getExposureForNat(exposures, item.nat);
          return (
            <div key={idx} className="col-6 col-md-3">
              <div className="casino-box-row">
                <div className="casino-nation-name">
                  {KING_IMGS[idx] && <img src={race20CardImg(KING_IMGS[idx])} alt="" />}{" "}
                  {rangeIcon(`demo${idx}`, item)}
                </div>{" "}
                <div className="casino-bl-box">
                  <div
                    className={`back casino-bl-box-item${lockCls(open, exp)}`}
                    onClick={() => click(item, item.b, item.nat, "back")}
                  >
                    <span className="casino-box-odd">{item.b}</span>
                  </div>{" "}
                  <div
                    className={`lay casino-bl-box-item${lockCls(open, exp)}`}
                    onClick={() => click(item, item.l, item.nat, "lay")}
                  >
                    <span className="casino-box-odd">{item.l}</span>
                  </div>
                </div>{" "}
                <div className={`casino-nation-name ${bookClass(exp)}`}>{formatBook(exp)}</div>
              </div>
            </div>
          );
        })}
      </div>{" "}
      <div className="row mt-2">
        <div className="col-12 col-md-4">
          {[0, 1].map((i) => {
            const item = fancy[i];
            if (!item) return null;
            const open = item.gstatus === "OPEN";
            const exp = lineBook(myBets, item.nat) ?? getExposureForNat(exposures, item.nat);
            return (
              <div key={i}>
                <div className="casino-yn">
                  <div />{" "}
                  <div className="casino-bl-box">
                    <div className="casino-bl-box-item yn-header">
                      <b>No</b>
                    </div>{" "}
                    <div className="casino-bl-box-item yn-header">
                      <b>Yes</b>
                    </div>
                  </div>
                </div>{" "}
                <div className="casino-odds-box casino-yn">
                  <div className="casino-odds-box-bhav">
                    <b>{item.nat}</b> {rangeIcon(`tp${i}`, item, "")}
                  </div>{" "}
                  <div className="casino-bl-box">
                    <div
                      className={`lay casino-bl-box-item${lockCls(open, exp)}`}
                      onClick={() => click(item, item.l, `${item.nat} No`, "lay")}
                    >
                      <span className="casino-box-odd">{item.l}</span>{" "}
                      <span>{open ? YN_SIZES[i].lay : "0"}</span>
                    </div>{" "}
                    <div
                      className={`back casino-bl-box-item${lockCls(open, exp)}`}
                      onClick={() => click(item, item.b, `${item.nat} Yes`, "back")}
                    >
                      <span className="casino-box-odd">{item.b}</span>{" "}
                      <span>{open ? YN_SIZES[i].back : "0"}</span>
                    </div>
                  </div>
                </div>{" "}
                <div className="casino-yn">
                  <div />{" "}
                  <div className="casino-bl-box">
                    <div className={`casino-nation-name ${bookClass(exp)}`}>{formatBook(exp)}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>{" "}
        <div className="col-12 col-md-8 win-with">
          <div className="row row5">
            {WIN_WITH_COLS.map(([top, bottom]) => (
              <div key={top} className="col-4">
                {winWithBox(top)} {winWithBox(bottom)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default BetTableRace20;
